/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import { useEffect, useState } from 'react';
import {
  TextField,
  Fab,
  Box,
  Typography,
  CircularProgress,
  Divider,
  MenuItem,
  Autocomplete
} from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import Page from 'components/templates/Page';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { ROUTES } from 'utils/constants';
import { useNavigate } from 'react-router-dom';
import { totalWorks } from 'mocks';
import { CompanyService } from 'services/company.service';
import { CompanyType, IndustryType } from 'types/company';
import { redirectToCompany } from 'utils/redirect';
import ScreenLoading from 'components/organisms/ScreenLoading';

import _ from 'lodash';
import { useStyles } from './styles';

function FormCompany() {
  const styles = useStyles();
  const navigate = useNavigate();
  const [dataLoaded, setDataLoaded] = useState(false);
  const [userCompany, setUserCompany] = useState<CompanyType>();
  const [industries, setIndustries] = useState<any>();
  const [states, setStates] = useState<any>();
  const [cities, setCities] = useState<any>();
  const [loading, setLoading] = useState(false);
  const [loadingRedirect, setLoadingRedirect] = useState(false);

  const schema = Yup.object().shape({
    fantasyName: Yup.string().required('Nome fantasia é um campo obrigatório.'),
    industry: Yup.object()
      .required('Segmento da empresa é um campo obrigatório.')
      .nullable(),
    amountIntervalWorkers: Yup.string()
      .required('Número de funcionários é um campo obrigatório.')
      .test(
        '',
        'Nº de funcionários é um campo obrigatório.',
        (el) => el !== totalWorks[0]
      ),
    state: Yup.string().nullable().required('Estado é um campo obrigatório.'),
    city: Yup.string().required('Cidade é um campo obrigatório.')
  });

  const title = (
    <span>
      Agora, vamos criar
      <br />
      sua conta empresa
    </span>
  );

  const saveGeneralInformation = async (values: any) => {
    setLoading(true);
    try {
      const companyId = userCompany?.id;
      await CompanyService.updateCompany(companyId ?? '', {
        ...userCompany,
        fantasyName: values?.fantasyName,
        amountIntervalWorkers: values?.amountIntervalWorkers,
        state: values?.state,
        city: values?.city,
        industryId: values?.industry?.id,
        zipCode: userCompany?.zipCode === '' ? null : userCompany?.zipCode,
        businessName:
          userCompany?.businessName === ''
            ? values?.fantasyName
            : userCompany?.businessName
      });
      navigate(`${ROUTES.CAREER}`);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const redirectUser = () => {
    setLoadingRedirect(true);
    redirectToCompany();
  };

  const getInfo = async () => {
    setLoading(true);
    try {
      const { data } = await CompanyService.getAssociatedCompany();
      setUserCompany(data[0]?.company);
      if (
        (data[0]?.company?.document &&
          data[0]?.company?.fantasyName &&
          data[0]?.company?.industry?.name &&
          data[0]?.company?.amountIntervalWorkers &&
          data[0]?.company?.state &&
          data[0]?.company?.city) ||
        data[0]?.company?.typeService === 'insights'
      ) {
        redirectUser();
        setLoading(false);
      }
      setTimeout(() => {
        setDataLoaded(true);
        setLoading(false);
      }, 1000);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchCitysPerState = async (state: any) => {
    try {
      const response = await fetch(
        `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${state?.id}/municipios`
      );
      const data = await response.json();
      setCities(data);
    } catch (error) {
      console.error('Ocorreu um erro ao obter as cidades:', error);
    }
  };

  const fetchStates = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://servicodados.ibge.gov.br/api/v1/localidades/estados`
      );
      const data = await response.json();
      setStates(data);
    } catch (error) {
      console.error('Ocorreu um erro ao obter as cidades:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIndustry = async () => {
    try {
      const { data } = await CompanyService.getIndustries();
      setIndustries(data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    getIndustry();
    fetchStates();
  }, []);

  useEffect(() => {
    getInfo();
    setLoading(false);
  }, []);

  return (
    <Page title={loadingRedirect || loading ? '' : title} subtitle="">
      {loading || !dataLoaded ? (
        <ScreenLoading />
      ) : (
        <Formik
          initialValues={{
            fantasyName: userCompany?.fantasyName ?? '',
            industry: userCompany?.industry || null,
            amountIntervalWorkers:
              userCompany?.amountIntervalWorkers || totalWorks[0],
            state: userCompany?.state || null,
            city: userCompany?.city ?? ''
          }}
          validationSchema={schema}
          onSubmit={(values) => saveGeneralInformation(values)}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            touched,
            values,
            setFieldValue,
            setTouched
          }) => (
            <Form className={styles.container}>
              <Box className={styles.containerName}>
                <Typography className={styles.emailLabel}>CNPJ</Typography>
                <Typography className={styles.email}>
                  {userCompany?.document}
                </Typography>
              </Box>
              <Divider />
              <Box className={styles.containerName}>
                <Typography className={styles.emailLabel}>
                  RAZÃO SOCIAL
                </Typography>
                <Box className={styles.containerBussiness}>
                  <Typography className={styles.email}>
                    {userCompany?.businessName}
                  </Typography>
                </Box>
              </Box>
              <TextField
                label="Nome Fantasia"
                value={values.fantasyName}
                error={touched.fantasyName && !!errors.fantasyName}
                helperText={touched.fantasyName && errors.fantasyName}
                name="fantasyName"
                onBlur={handleBlur}
                onChange={handleChange}
                className={styles.filter}
              />
              <Autocomplete
                id="industry"
                options={industries?.filter(
                  (industry: IndustryType) => industry?.name !== 'Outros'
                )}
                getOptionLabel={(option: IndustryType) =>
                  typeof option === 'string' ? option : option.name || ''
                }
                isOptionEqualToValue={(option: IndustryType) =>
                  option.name === values.industry?.name
                }
                className={styles.autoComplete}
                onChange={(_, value) =>
                  setFieldValue('industry', !value ? '' : value)
                }
                value={
                  values?.industry?.id === 1
                    ? { name: '', id: 0 }
                    : values.industry
                }
                renderInput={(params) => (
                  <TextField
                    error={Boolean(touched.industry && errors.industry)}
                    helperText={touched.industry && errors.industry}
                    label="Segmento"
                    name="industry"
                    {...params}
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: 'off',
                      maxLength: 22
                    }}
                  />
                )}
              />
              <TextField
                error={Boolean(
                  touched.amountIntervalWorkers && errors.amountIntervalWorkers
                )}
                onBlur={handleBlur}
                onChange={handleChange}
                helperText={
                  touched.amountIntervalWorkers && errors.amountIntervalWorkers
                }
                fullWidth
                select
                value={values.amountIntervalWorkers}
                name="amountIntervalWorkers"
                className={styles.filter}
                disabled={loading}
              >
                {totalWorks.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
              <Autocomplete
                id="state"
                options={states}
                getOptionLabel={(option: any) =>
                  typeof option === 'string' ? option : option.nome || ''
                }
                isOptionEqualToValue={(option: any) =>
                  option.nome === values.state
                }
                className={styles.autoComplete}
                onChange={(_, value: any) => {
                  setFieldValue('state', !value ? '' : value?.nome);
                  fetchCitysPerState(value);
                }}
                value={values.state}
                renderInput={(params) => (
                  <TextField
                    error={Boolean(touched.state && errors.state)}
                    helperText={touched.state && errors.state}
                    label="Estado"
                    name="state"
                    {...params}
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: 'off',
                      maxLength: 22
                    }}
                  />
                )}
              />
              <Box display="flex" gap="15px">
                <Autocomplete
                  id="city"
                  options={cities ?? []}
                  getOptionLabel={(option: any) =>
                    typeof option === 'string' ? option : option?.nome
                  }
                  isOptionEqualToValue={(option: any) =>
                    option.nome === values?.city
                  }
                  className={styles.autoComplete}
                  onChange={(_, value: any) => {
                    setFieldValue(
                      'city',
                      !value
                        ? ''
                        : typeof value === 'string'
                        ? value
                        : value?.nome
                    );
                  }}
                  onBlur={() =>
                    setTouched({
                      ...touched,
                      city: true
                    })
                  }
                  value={values?.city}
                  renderInput={(params) => (
                    <TextField
                      error={Boolean(touched?.city && errors?.city)}
                      helperText={touched?.city && errors?.city}
                      label="Cidade"
                      name="city"
                      value={values?.city}
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        autoComplete: 'off'
                      }}
                    />
                  )}
                />
              </Box>
              <Box className={styles.buttonContainer}>
                <Fab type="submit" disabled={loading}>
                  {loading ? (
                    <CircularProgress style={{ color: '#fff' }} size={24} />
                  ) : (
                    <ArrowForward />
                  )}
                </Fab>
              </Box>
            </Form>
          )}
        </Formik>
      )}
    </Page>
  );
}

export default FormCompany;
